import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import {
  createSupplierMapData,
  deleteSupplierMapData,
  listSupplierMaps,
  updateSupplierMapData,
  listSupplierMapsByRpmAcctSupplierId,
  listSupplierMapsBySFDCSupplierId
} from '../Api/SupplierMap/SupplierMapApi';
import { addError, clearError } from './Error';
import { User } from './User';

export interface SupplierMap {
  id?: string;
  supplier_id: string;
  salesforce_supplier_name: string;
  supplier_sfid?: string;
  rpm_supplier_name: string;
  rpm_supplier_id: string;
  note: string;
  createdBy?: User;
  createdByDisplay?: string;
  createdAt?: string;
}

const tallySupplierMapsSubject = new BehaviorSubject<SupplierMap[]>([] as SupplierMap[]);
const tallySupplierMapsLoadingSubject = new BehaviorSubject<boolean>(false);
const supplierMapsByRpmAcctSupplierIdSubject = new BehaviorSubject<SupplierMap[]>([] as SupplierMap[]);
const supplierMapsBySFDCSupplierIdSubject = new BehaviorSubject<SupplierMap[]>([] as SupplierMap[]);
const supplierMapsByRpmAcctSupplierIdLoadingSubject = new BehaviorSubject<boolean>(false);
const supplierMapsBySFDCSupplierIdLoadingSubject = new BehaviorSubject<boolean>(false);

export const getTallySupplierMapsSubject = (): BehaviorSubject<SupplierMap[]> => tallySupplierMapsSubject;
export const getTallySupplierMapsLoadingSubject = (): BehaviorSubject<boolean> => tallySupplierMapsLoadingSubject;
export const getSupplierMapsByRpmAcctSupplierIdSubject = (): BehaviorSubject<SupplierMap[]> =>
  supplierMapsByRpmAcctSupplierIdSubject;
export const getSupplierMapsBySFDCSupplierIdSubject = (): BehaviorSubject<SupplierMap[]> =>
  supplierMapsBySFDCSupplierIdSubject;
export const getSupplierMapsByRpmAcctSupplierIdLoadingSubject = (): BehaviorSubject<boolean> =>
  supplierMapsByRpmAcctSupplierIdLoadingSubject;
export const getSupplierMapsBySFDCSupplierIdLoadingSubject = (): BehaviorSubject<boolean> =>
  supplierMapsBySFDCSupplierIdLoadingSubject;

export const fetchSupplierMaps = async () => {
  tallySupplierMapsLoadingSubject.next(true);

  const { data, error } = await listSupplierMaps();

  if (data) {
    clearError('supplier-map');
    const formatted = data.map((sm) => {
      if (sm.createdBy) {
        sm.createdByDisplay = `${sm.createdBy.firstName} ${sm.createdBy.lastName}`;
      }
      return sm;
    });
    tallySupplierMapsSubject.next(formatted);
  }
  if (error) addError('supplier-map', error.message);

  tallySupplierMapsLoadingSubject.next(false);
};

export const createSupplierMap = async (supplierMap: Partial<SupplierMap>) => {
  const supplierMapData: SupplierMap = { ...({} as SupplierMap), ...supplierMap };

  const { data, error } = await createSupplierMapData(supplierMapData);

  if (data) {
    clearError('supplier-map');
    fetchSupplierMaps();
  }
  if (error) addError('supplier-map', error.message);

  return { data, error };
};

export const updateSupplierMap = async (update: SupplierMap) => {
  const { data, error } = await updateSupplierMapData(update);
  if (data) {
    clearError('supplier-map');
    fetchSupplierMaps();
  }
  if (error) addError('supplier-map', error.message);

  return { data, error };
};

export const deleteSupplierMap = async (id: string) => {
  const { data, error } = await deleteSupplierMapData(id);

  if (data) {
    clearError('supplier-map');
    fetchSupplierMaps();
  }
  if (error) addError('supplier-map', error.message);
};

export const fetchSupplierMapsByRpmAcctSupplierId = async (id: string): Promise<SupplierMap[] | undefined> => {
  supplierMapsByRpmAcctSupplierIdLoadingSubject.next(true);
  const { data, error } = await listSupplierMapsByRpmAcctSupplierId(id);
  supplierMapsByRpmAcctSupplierIdLoadingSubject.next(false);

  if (error) addError('supplier-map', error.message);

  if (data) {
    supplierMapsByRpmAcctSupplierIdSubject.next(data);
    return data;
  }
};

export const fetchSupplierMapsBySFDCSupplierId = async (id: string): Promise<SupplierMap[] | undefined> => {
  supplierMapsBySFDCSupplierIdLoadingSubject.next(true);
  const { data, error } = await listSupplierMapsBySFDCSupplierId(id);
  supplierMapsBySFDCSupplierIdLoadingSubject.next(false);

  if (error) addError('supplier-map', error.message);

  if (data) {
    supplierMapsBySFDCSupplierIdSubject.next(data);
    return data;
  }
};
